import React from 'react';
import classNames from 'classnames';
import { Icon } from 'js/design_system/Icon';
import { trackClick, Options as TrackClickOptions } from 'js/utils/trackClick';

import 'app/assets/stylesheets/design_system/pill_button.scss';

// Shared between PillButton and PillButtonLink
export type BasePillProps = {
  size?: 'sm' | 'lg';
  icon?: string | React.ReactNode;
  iconRight?: string | React.ReactNode;
  trackClick?: TrackClickOptions;
};

type BasePillButtonProps = BasePillProps & {
  selected?: boolean;
};

type PillButtonProps = BasePillButtonProps &
  React.ComponentPropsWithoutRef<'button'>;

/**
 * A pill-shaped button for selection states, tags, or keyword filtering
 *
 * Props:
 * - `size`: Size of the button - one of 'sm' or 'lg' (default: 'lg')
 * - `icon`: The name of a Material Symbols icon (string) or an arbitrary React component to render as the button's icon (left of the text)
 * - `iconRight`: The name of a Material Symbols icon (string) or an arbitrary React component to render as the button's icon (right of the text)
 * - `selected`: Give the button thicker border to indicate it's selected
 * - `trackClick`: Options for tracking clicks on this button
 *
 * ## Basic Usage
 *
 * @example
 *
 * ```tsx
 * import { PillButton } from 'js/design_system/PillButton';
 *
 * <PillButton onClick={clickHandler}>
 *   Click me!
 * </PillButton>
 * ```
 */
export const PillButton = React.forwardRef<
  HTMLButtonElement,
  React.PropsWithChildren<PillButtonProps>
>(
  (
    {
      size = 'lg',
      icon,
      iconRight,
      selected,
      className,
      children,
      trackClick: trackClickOptions,
      onClick,
      ...restProps
    },
    ref,
  ) => {
    const classes = classNames('ds-pill-button', className, {
      'ds-pill-button--small': size === 'sm',
      'ds-pill-button--selected': selected,
    });

    let iconView = null;
    if (icon) {
      iconView =
        typeof icon === 'string' ? (
          <Icon name={icon} size={size === 'sm' ? 16 : 20} />
        ) : (
          icon
        );
    }

    let iconRightView = null;
    if (iconRight) {
      iconRightView =
        typeof iconRight === 'string' ? (
          <Icon name={iconRight} size={size === 'sm' ? 16 : 20} />
        ) : (
          iconRight
        );
    }

    const handleClick = trackClickOptions
      ? (event: React.MouseEvent<HTMLButtonElement>) => {
          if (trackClickOptions) {
            trackClick(trackClickOptions);
          }
          return onClick?.(event);
        }
      : onClick;

    return (
      <button
        className={classes}
        {...restProps}
        onClick={handleClick}
        ref={ref}
      >
        {iconView ? iconView : null}
        {children}
        {iconRightView ? iconRightView : null}
      </button>
    );
  },
);
